import {
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  Inject,
  OnDestroy,
  OnInit,
  Output,
  PLATFORM_ID,
  RESPONSE_INIT,
  SecurityContext,
  DOCUMENT,
  ChangeDetectionStrategy,
} from '@angular/core';
import { EnvToken, SeoSegmentsToken } from '@app/core/env.tokens';
import {
  ActivatedRoute,
  ParamMap,
  Router,
  RouterOutlet,
} from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';

// Models
import { Blog } from '@models/blog.model';
import { HowTo } from '@models/guides/howToGuide.model';
import { Hub } from '@models/hub.model';
import { Category } from '@models/category/Category.model';
import { PriceGuide } from '@models/guides/priceGuide.model';
import { ReviewAggregationForTagResponse } from '@models/tag/reviewAggregationForTagResponse';
import { Tip } from '@models/guides/tips.model';
import { Vertical } from '@models/vertical/vertical.model';

// Interface
import { CollectionPageSchema } from '@app/pages/hub-page/interface/collectionPageSchema.type.interface';
import { SubHub } from '@app/pages/hub-page/interface/subHub.type.interface';

// Service
import { MainCategoriesService } from '@services/http/mainCategories.service';
import { SEOService } from '@services/logic/seo.service';
import { TagService } from '@services/http/tag.service';
import { MiniVerticalService } from '@services/http/mini-verticals.service';
import { LocalServicePageService } from '@services/http/LocalServicePage.service';

import { generateEntityUrl } from '@helpers/generateEntityUrl';
import { indexPagePath } from '@helpers/indexPagePath';
// Modules
import { CommonModule, isPlatformBrowser } from '@angular/common';

// Comps
import { CustomCardComponent } from '@components/shared/custom-card/custom-card.component';
import { GenericCardComponent } from '@components/shared/generic-card/generic-card.component';
import { HeaderLoaderComponent } from '@components/shared/loaders/header-loader/header-loader.component';
import { HubHeaderComponent } from '@components/hub-header/hub-header.component';
import { LinksContainerComponent } from '@components/links-container/links-container.component';
import { FooterComponent } from '@components/shared/footer/footer.component';
import { AffaldHubHierarchyComponent } from '@components/affald-hub-hierarchy/affald-hub-hierarchy.component';
import {
  DeepLinkButtonsComponent,
  DeepLinkButton,
} from '@components/shared/deep-link-buttons/deep-link-buttons.component';

// Pipes
import { JsonLdPipe } from '@app/pipes/json-ld-pipe';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { MatIconModule } from '@angular/material/icon';
import { ScrollProgressBarDirective } from '@app/directives/scroll-progress-bar-directive';
import { takeUntil, catchError } from 'rxjs/operators';
import { Subject, of } from 'rxjs';
import {
  CategoryAggregateResult,
  HeadCategoryResponse,
} from '@app/pages/hub-page/interface/headCategory.response.interface';
import { SafePipe } from '@app/pipes/safe';
import { AIPriceGuide } from '@models/guides/aiPriceGuide.model';

@Component({
  imports: [
    CustomCardComponent,
    GenericCardComponent,
    FooterComponent,
    HubHeaderComponent,
    HeaderLoaderComponent,
    LinksContainerComponent,
    CommonModule,
    MatIconModule,
    JsonLdPipe,
    TranslatePipe,
    ScrollProgressBarDirective,
    SafePipe,
    RouterOutlet,
    DeepLinkButtonsComponent,
    AffaldHubHierarchyComponent,
  ],
  providers: [SEOService, TranslatePipe],
  selector: 'app-hub-page',
  templateUrl: './hub-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./hub-page.component.scss'],
})
export class HubPageComponent implements OnInit, OnDestroy {
  protected readonly environment = inject(EnvToken);
  protected readonly seoSegments = inject(SeoSegmentsToken);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly localServicePageService = inject(LocalServicePageService);

  private readonly responseInit = inject(RESPONSE_INIT, { optional: true });
  @Output() descriptionHeader!: string;
  @Output() headerImg: string = '';
  @Output() titleHeader!: string;
  partsOfUrl: { name: string; url: string }[] = [];

  // @Params
  headCategorySlug!: string;
  subCategorySlug?: string | null;
  subSubCategorySlug?: string | null;
  url: string = '';
  private destroy$: Subject<void> = new Subject<void>();
  /**
   * Emits at the start of every `checkLocalServicesAvailability()` call to
   * cancel any previous in-flight count request. This component is reused
   * across route param changes (see `subscribeToParams()`), so without this
   * an out-of-order response for a stale category could set `hasLocalServices`
   * for the new one.
   */
  private localServicesAvailabilityChange$: Subject<void> = new Subject<void>();
  private isBrowser: boolean;
  public localServiceIndexLink: string = this.seoSegments.suffix.hubIndex;
  public miniVerticalIndexLink: string = this.seoSegments.suffix.hubIndex;
  public hasMiniVerticals: boolean = false;
  /** False until the count endpoint confirms the category retains companies. */
  public hasLocalServices: boolean = false;
  subTitle: string | null | undefined = '';
  isAffaldPath: boolean = false;
  isAffaldHavePath: boolean = false;
  additionalLinks: { link: string; text: string }[] = [];

  // Entities
  public articleList: Blog[] = [];
  public guideList: (HowTo | Blog)[] = [];
  public howToList: HowTo[] = [];
  public headCategory: Category = {};
  public hub?: Hub;
  public priceGuideList: PriceGuide[] = [];
  public reviewCount: ReviewAggregationForTagResponse;
  public subHubs: SubHub[] = [];
  public tipsAndTrickList: Tip[] = [];
  public vertical?: Vertical;
  public aIPriceGuides: AIPriceGuide[] = [];
  public mostRelevantAiPriceGuideId: undefined | number;

  // Entity Titles
  public guideTitle = this.translatePipe.transform('PAGES.HUB.GUIDE_TITLE');
  public tipsTitle =
    this.translatePipe.transform('PAGES.HUB.TIPS_TITLE') + this.subTitle;
  public tipsDesc = this.translatePipe.transform('PAGES.HUB.TIPS_DESC');
  public priceGuideTitle = '';
  public priceDataTitle = '';
  public verticalTitle = '';

  public subHubTitle = this.translatePipe.transform('PAGES.HUB.SUB_HUB_TITLE');

  tableOfContentExpanded = false;
  h2Texts: { id: string; text: string }[] = [];

  isLoading: boolean = false;
  innerWidth: number = 480;

  // Deep link buttons
  public deepLinkButtons: DeepLinkButton[] = [];

  // Schema
  public breadcrumbSchema: any;
  public collectionPageSchema: CollectionPageSchema | undefined;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    @Inject(DOCUMENT) private document: Document,
    private activatedRoute: ActivatedRoute,
    private mainCatService: MainCategoriesService,
    private router: Router,
    private sanitizer: DomSanitizer,
    private seoService: SEOService,
    private tagService: TagService,
    private miniVerticalService: MiniVerticalService,
    private translatePipe: TranslatePipe,
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.reviewCount = new ReviewAggregationForTagResponse();
  }

  get shouldDisplayPriceComp(): boolean {
    return !!this.vertical?.priceGuideId || !!this.mostRelevantAiPriceGuideId;
  }

  private removeDuplicateParams(urlPath: string): string {
    if (!urlPath) return urlPath;

    const segments = urlPath.split('/').filter((segment) => segment.length > 0);
    const cleanedSegments: string[] = [];

    for (let index = 0; index < segments.length; index++) {
      const currentSegment = segments[index];
      const nextSegment = segments[index + 1];

      // Only add current segment if it's not the same as the next one
      if (currentSegment !== nextSegment) {
        cleanedSegments.push(currentSegment);
      }
    }

    return '/' + cleanedSegments.join('/');
  }

  private checkAndRedirectForDuplicates(): void {
    if (!this.isBrowser) return;

    const currentUrl = this.router.url;
    const cleanedUrl = this.removeDuplicateParams(currentUrl);

    // If the cleaned URL is different from current URL, redirect
    if (cleanedUrl !== currentUrl) {
      const fullCleanedUrl = `${this.environment.domain}${cleanedUrl}`;
      // Use 303 redirect (See Other) for SEO-friendly redirect
      window.location.replace(fullCleanedUrl);
    }
  }

  ngOnInit() {
    if (this.isBrowser) {
      this.innerWidth = window.innerWidth;
      if (innerWidth > 990) {
        this.tableOfContentExpanded = true;
      }
    }

    // Check for duplicate parameters and redirect if found
    this.checkAndRedirectForDuplicates();

    this.subscribeToParams();
  }

  private subscribeToParams() {
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe((params: ParamMap) => {
        this.headCategorySlug = params.get('headCategory')!;
        this.subCategorySlug = params.get('subCategory');
        this.subSubCategorySlug = params.get('subSubCategory');
        this.fetchCategory();
        this.setIndexPathLocalServices();
        this.setIndexPathMiniVerticals();
        this.url = this.router.url.split('/')[1];
        this.changeDetectorRef.markForCheck();
      });
  }

  @HostListener('window:resize')
  onResize() {
    if (this.isBrowser) {
      this.innerWidth = window.innerWidth;
      if (this.innerWidth > 990) {
        this.tableOfContentExpanded = true;
      }
    }
  }

  public expandTableOfContent() {
    this.tableOfContentExpanded = !this.tableOfContentExpanded;
  }

  public scrollTo(event: MouseEvent, elementId?: string): void {
    if (this.isBrowser && elementId) {
      event.preventDefault();
      const element: HTMLElement | null =
        this.document.getElementById(elementId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }

  public scrollToSection(sectionId: string): void {
    if (this.isBrowser) {
      const element: HTMLElement | null =
        this.document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }

  private fetchCategory() {
    this.isLoading = true;
    this.mainCatService
      .fetchMainCategoryIncludeEntities(
        this.headCategorySlug,
        this.subCategorySlug,
        this.subSubCategorySlug,
        {
          howToLimit: 200,
          priceGuideLimit: 200,
          tipsLimit: 30,
          blogLimit: 200,
          aiPriceGuideLimit: 20,
        },
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: HeadCategoryResponse) => {
          // Header
          this.descriptionHeader = response.category?.description ?? '';
          this.titleHeader = response.category?.hub?.hubTitle ?? '';
          this.vertical = response.category?.vertical;
          const aggregate: CategoryAggregateResult =
            response.hubPageAggregateResult || {};
          this.headerImg =
            response.category?.imgUrl || aggregate?.heroImg?.url || '';
          this.headCategory = response.category || {};
          // Entity Data
          this.articleList =
            aggregate?.articles?.map((blog: Blog) => new Blog(blog)) || [];
          this.howToList =
            aggregate?.howTos?.map((howTo) => new HowTo(howTo)) || [];
          this.priceGuideList =
            aggregate?.priceGuides?.map(
              (priceGuide: PriceGuide): PriceGuide =>
                new PriceGuide(priceGuide),
            ) || [];
          this.aIPriceGuides = aggregate?.aiPriceGuides ?? [];
          this.mostRelevantAiPriceGuideId = this.aIPriceGuides.find(
            (x: AIPriceGuide) => x?.mostRelevantPriceGuide?.id,
          )?.mostRelevantPriceGuide?.id;

          // Prepare price guide data with the price guide at the beginning if needed
          this.preparePriceGuideData();

          // Many tips lack the `tips` field; the instance is what marks them as tips.
          this.tipsAndTrickList =
            aggregate?.tipsAndTricks?.map((tip: Tip) =>
              Object.assign(new Tip(), tip),
            ) ?? [];
          this.subHubs = response?.category?.subHubsCats
            ? (response.category?.subHubsCats ?? [])
            : (aggregate?.subHubsCats ?? []);
          this.subTitle = response?.category?.title
            ? response?.category?.title?.toLowerCase()
            : this.subCategorySlug?.toLowerCase();
          this.hub = response.category?.hub;
          this.guideList = [...this.howToList, ...this.articleList];

          this.priceGuideTitle =
            this.translatePipe.transform('PAGES.HUB.PRICE_GUIDE_TITLE') +
            this.subTitle;
          this.priceDataTitle = this.translatePipe
            .transform('PAGES.HUB.PRICE_DATA_TITLE')
            .replace('{{replace}}', this.subTitle);
          this.verticalTitle =
            this.translatePipe.transform('PAGES.HUB.VERTICAL_TITLE') +
            this.subTitle;

          if (this.vertical?.tagId) {
            this.fetchReviewCount(this.vertical?.tagId);
          }
          if (this.isBrowser && this.hub?.content && this.hub.content['main']) {
            try {
              const originalContent = this.hub.content['main'];
              const modifiedHtmlString = this.addIDsToH2s(originalContent);
              this.hub.content['main'] = modifiedHtmlString;

              // Extract H2 texts from the modified HTML
              this.h2Texts = this.getH2FromHtmlString(modifiedHtmlString);
            } catch (error) {}
          }
          this.resolveMiniVerticalsAvailability(response.hasMiniVerticals);
          this.injectSchemaMarkup();
          this.setMeta();
          this.setBreadCrumb();
          this.setupDeepLinkButtons();
          this.isLoading = false;
          if (this.vertical) {
            this.vertical.relatedVNC = response.category?.subCategories;
          }
          this.changeDetectorRef.markForCheck();
        },
        error: (error: any): any => {
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
          // handle 301 redirects
          if (error.status === 301) {
            return this.router.navigate([error.error.url]);
          }
          // Only a genuine "this hub does not exist" (404) is a cacheable
          // not-found. Other 4xx (429 rate-limit, 401/403) plus 5xx/timeouts
          // are transient upstream failures — emit a retryable 503 so the SSR
          // layer does NOT freeze them as a permanent 404 (see server.ts
          // notFoundCache). Caching a transient miss takes a valid page offline.
          if (error.status === 404) {
            if (this.responseInit) {
              this.responseInit.status = 404;
            }
            // skipLocationChange: render the 404 on the requested URL — an
            // in-render navigation during SSR would otherwise be treated as an
            // HTTP redirect, which must be 3xx (404 throws → empty response).
            return this.router.navigate(['/404'], {
              skipLocationChange: true,
            });
          }
          if (this.responseInit) {
            this.responseInit.status = 503;
          }
        },
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.localServicesAvailabilityChange$.next();
    this.localServicesAvailabilityChange$.complete();
  }

  private getH2FromHtmlString(
    htmlString: string,
  ): { id: string; text: string }[] {
    const div: HTMLDivElement = this.document.createElement('div');
    if (div) {
      div.innerHTML = htmlString;
    }
    const h2Elements: HTMLCollectionOf<HTMLHeadingElement> =
      div.getElementsByTagName('h2');
    return Array.from(h2Elements).map((h2: HTMLHeadingElement) => ({
      id: h2.id,
      text: h2.textContent || '',
    }));
  }

  private addIDsToH2s(htmlString: any): string {
    try {
      const htmlContent =
        typeof htmlString === 'string'
          ? htmlString
          : this.sanitizer.sanitize(SecurityContext.HTML, htmlString) || '';

      // Parse the HTML string into a Document
      const parsedDocument: Document = new DOMParser().parseFromString(
        htmlContent,
        'text/html',
      );

      // Get all H2 elements
      const headings = parsedDocument.getElementsByTagName('h2');

      // Add a unique ID to each H2
      Array.from(headings).forEach((heading, index) => {
        heading.id = `h2-${index}`;
      });

      return new XMLSerializer().serializeToString(parsedDocument);
    } catch (error) {
      // Return the original content if there's an error
      return typeof htmlString === 'string' ? htmlString : '';
    }
  }

  private fetchReviewCount(tagId: number) {
    this.isLoading = true;

    this.tagService
      .totalReviewsForTasksTag(tagId)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (response) => {
          if (response) {
            this.reviewCount = response;
          }
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        },
        () => {
          this.isLoading = false;
          this.changeDetectorRef.markForCheck();
        },
      );
  }

  public getMainContent(): string {
    if (this.hub && this.hub.content) {
      const content = this.hub.content['main'];
      return typeof content === 'string' ? content : '';
    }
    return '';
  }

  private setBreadCrumb() {
    const mainCategory = this.headCategory?.subCategories?.find(
      (subCategory) => subCategory?.mainCategory,
    );
    this.partsOfUrl.push({
      name: mainCategory?.mainCategory?.title ?? this.headCategorySlug,
      url: `${this.environment.domain}/${this.headCategorySlug}/${this.seoSegments.suffix.hubIndex}`,
    });
    if (
      this.headCategorySlug &&
      this.subCategorySlug &&
      !this.subSubCategorySlug
    ) {
      this.partsOfUrl.push({
        name: this.subCategorySlug,
        url: `${this.environment.domain}/${this.headCategorySlug}/${this.subCategorySlug}/${this.seoSegments.suffix.hubIndex}`,
      });
    } else if (
      this.subSubCategorySlug &&
      this.subCategorySlug &&
      this.headCategorySlug
    ) {
      this.partsOfUrl.push({
        name: this.headCategory?.title ?? this.subSubCategorySlug,
        url: `${this.environment.domain}/${this.headCategorySlug}/${this.subCategorySlug}/${this.subSubCategorySlug}/${this.seoSegments.suffix.hubIndex}`,
      });
    }
    this.injectBreadCrumbSchema();
  }

  private setMeta() {
    this.seoService.setPageMeta({
      metaTitle: this.headCategory?.title_tag ?? this.headCategory?.title ?? '',
      metaDesc: this.headCategory?.meta_description ?? '',
      img: this.headerImg ?? '',
      canonicalUrl: `${this.environment.domain}${this.router.url}`,
    });
  }

  private setIndexPathLocalServices(): void {
    let basePath = this.headCategorySlug;
    if (this.subCategorySlug) {
      basePath += '/' + this.subCategorySlug;
    }
    if (this.subSubCategorySlug) {
      basePath += '/' + this.subSubCategorySlug;
    }
    this.isAffaldPath = basePath === 'affald';
    this.isAffaldHavePath = basePath === 'affald/affald-have';

    // The four affald index pages have been consolidated into the region →
    // kommune → by hierarchy rendered on this hub (<app-affald-hub-hierarchy>);
    // those index URLs now 301 to /affald/hub, so they are no longer linked.
    this.additionalLinks = [];

    this.localServiceIndexLink = indexPagePath(
      this.seoSegments.indexPage.localServices,
      [this.headCategorySlug, this.subCategorySlug, this.subSubCategorySlug],
    );

    this.checkLocalServicesAvailability();
  }

  /**
   * The index 404s when the category retains no companies after the keep-list
   * prune, so only link it when there is something to list. A failed count is
   * treated as "nothing" — better a missing link than one pointing at a 404.
   */
  private checkLocalServicesAvailability(): void {
    // Cancel any previous in-flight count request — this component is reused
    // across route param changes, so a stale response must never be able to
    // set `hasLocalServices` for the category we are now checking.
    this.localServicesAvailabilityChange$.next();
    this.hasLocalServices = false;
    this.changeDetectorRef.markForCheck();

    const deepestCategory =
      this.subSubCategorySlug || this.subCategorySlug || this.headCategorySlug;
    if (!deepestCategory) {
      return;
    }
    this.localServicePageService
      .fetchLocalServiceCountByCategorySlug(deepestCategory)
      .pipe(
        takeUntil(this.destroy$),
        takeUntil(this.localServicesAvailabilityChange$),
        catchError(() => of({ count: 0 })),
      )
      .subscribe((response: { count: number }) => {
        this.hasLocalServices = (response?.count ?? 0) > 0;
        this.changeDetectorRef.markForCheck();
      });
  }

  private setIndexPathMiniVerticals(): void {
    this.miniVerticalIndexLink = indexPagePath(
      this.seoSegments.indexPage.miniVerticals,
      [this.headCategorySlug, this.subCategorySlug, this.subSubCategorySlug],
    );
  }

  /**
   * The hub aggregate response carries `hasMiniVerticals` computed inline by
   * the backend. Only payloads from before that flag existed (older backend,
   * or its up-to-24h response cache) lack it — those fall back to probing the
   * mini-verticals index endpoint.
   */
  private resolveMiniVerticalsAvailability(flag: boolean | undefined): void {
    if (!this.headCategorySlug) {
      this.hasMiniVerticals = false;
      return;
    }
    if (typeof flag === 'boolean') {
      this.hasMiniVerticals = flag;
      return;
    }
    this.checkMiniVerticalsAvailability();
  }

  private checkMiniVerticalsAvailability(): void {
    const categorySlug =
      this.subSubCategorySlug || this.subCategorySlug || this.headCategorySlug;
    if (!categorySlug) {
      this.hasMiniVerticals = false;
      return;
    }
    this.miniVerticalService
      .hasMiniVerticalsByCategorySlug(categorySlug)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (hasMiniVerticals) => {
          this.hasMiniVerticals = hasMiniVerticals;
          this.changeDetectorRef.markForCheck();
        },
        error: () => {
          this.hasMiniVerticals = false;
          this.changeDetectorRef.markForCheck();
        },
      });
  }

  private injectBreadCrumbSchema() {
    this.breadcrumbSchema = {
      '@context': 'http://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [],
    };
    this.partsOfUrl.forEach((part, index) => {
      this.breadcrumbSchema.itemListElement.push({
        '@type': 'ListItem',
        // Google requires 1-based breadcrumb positions
        position: index + 1,
        item: {
          '@id': part.url,
          name: part.name,
        },
      });
    });
  }
  private injectSchemaMarkup() {
    const data = [
      ...(this.articleList?.length > 0 ? this.articleList : []),
      ...(this.howToList?.length > 0 ? this.howToList : []),
      ...(this.priceGuideList?.length > 0 ? this.priceGuideList : []),
      ...(this.tipsAndTrickList?.length > 0 ? this.tipsAndTrickList : []),
    ] as any[];
    if (this.vertical) {
      data.push(this.vertical);
    }

    const categoryTitle = this.headCategory?.title || '';
    const hubMetaDesc = this.hub?.metaDesc || '';
    const schemaDesc = `${this.translatePipe.transform(
      'PAGES.HUB.SCHEMA.DESC',
    )} ${hubMetaDesc}`;

    this.collectionPageSchema = {
      '@context': 'http://schema.org',
      '@type': 'CollectionPage',
      name: categoryTitle,
      description: schemaDesc,
      mainEntity: {
        '@type': 'ItemList',
        name: categoryTitle,
        description: schemaDesc,
        numberOfItems: data.length,
        itemListElement: data.map((entity, index) => {
          return {
            '@type': 'ListItem',
            position: index,
            item: {
              '@type': 'Article',
              name: entity?.title || entity?.name || entity?.int || '',
              description:
                entity?.description ||
                entity?.content?.sub_header ||
                entity?.meta_description ||
                entity?.mT ||
                entity?.title ||
                '',
              image: entity?.imgUrl || '',
              url: `${this.environment.domain}${generateEntityUrl(
                this.seoSegments,
                entity,
                this.headCategorySlug || '',
                this.subCategorySlug || '',
              )}`,
            },
          };
        }),
      },
    };
  }

  /**
   * Prepares the price guide data by adding the price guide at the beginning
   * This ensures consistent rendering between server and client
   */
  private preparePriceGuideData(): void {
    if (this.shouldDisplayPriceComp && this.priceGuideList.length) {
      const priceGuideId =
        this.vertical?.priceGuideId || this.mostRelevantAiPriceGuideId;

      if (priceGuideId) {
        const priceGuide = this.priceGuideList.find(
          (p): p is PriceGuide => 'id' in p && p.id === priceGuideId,
        );

        const syntheticTitle = this.translatePipe.transform(
          'PAGES.HUB.SYNTHETIC_PRICE_GUIDE_TITLE',
        );
        const alreadyHasPriceGuide =
          this.priceGuideList.length > 0 &&
          this.priceGuideList[0].title === syntheticTitle;

        if (priceGuide && !alreadyHasPriceGuide) {
          // Create a new array instead of modifying the existing one
          this.priceGuideList = [
            {
              ...priceGuide,
              title: syntheticTitle,
              meta_description: this.translatePipe.transform(
                'PAGES.HUB.SYNTHETIC_PRICE_GUIDE_DESC',
              ),
            } as PriceGuide,
            ...this.priceGuideList,
          ];
        }
      }
    }
  }

  private setupDeepLinkButtons(): void {
    this.deepLinkButtons = [];

    // Add Guides button if guides exist
    if (this.guideList && this.guideList.length > 0) {
      this.deepLinkButtons.push({
        label: this.translatePipe.transform('PAGES.HUB.DEEPLINK_GUIDES'),
        action: () => this.scrollToSection('guides-section'),
        show: true,
      });
    }

    // Add Price Guides button if price guides exist
    if (this.priceGuideList && this.priceGuideList.length > 0) {
      this.deepLinkButtons.push({
        label: this.translatePipe.transform('PAGES.HUB.DEEPLINK_PRICE_GUIDES'),
        action: () => this.scrollToSection('price-guides-section'),
        show: true,
      });
    }

    // Add Tips button if tips exist
    if (this.tipsAndTrickList && this.tipsAndTrickList.length > 0) {
      this.deepLinkButtons.push({
        label: this.translatePipe.transform('PAGES.HUB.DEEPLINK_TIPS_TRICKS'),
        action: () => this.scrollToSection('tips-section'),
        show: true,
      });
    }

    // Add Services button if vertical exists
    if (this.vertical) {
      this.deepLinkButtons.push({
        label: this.translatePipe.transform('PAGES.HUB.DEEPLINK_SERVICES'),
        action: () => this.scrollToSection('services-section'),
        show: true,
      });
    }
  }
}
